import React, { useState } from 'react';
import { Menu, X, Globe, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';

const langs = [
  { code: 'en', label: 'English', short: 'EN' },
  { code: 'fr', label: 'Français', short: 'FR' },
  { code: 'ar', label: 'العربية', short: 'AR' }
];

const LangSwitcher = ({ dark }) => {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const current = langs.find((l) => l.code === lang);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 px-3 py-2 border ${dark ? 'border-slate-300 text-slate-700 hover:border-[#f7a11a]' : 'border-white/30 text-white hover:border-[#f7a11a]'} text-xs font-semibold tracking-widest transition-colors`}
      >
        <Globe className="w-4 h-4" />
        {current?.short}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-2 w-40 bg-white shadow-xl border border-slate-200 z-20">
            {langs.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`w-full flex items-center justify-between px-4 py-3 text-sm text-slate-700 hover:bg-slate-50 ${lang === l.code ? 'font-bold text-[#0b2545]' : ''}`}
              >
                <span>{l.label}</span>
                {lang === l.code && <Check className="w-4 h-4 text-[#f7a11a]" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const Header = () => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';

  const navItems = [
    { key: 'home', href: '#home' },
    { key: 'products', href: '#products' },
    { key: 'about', href: '#about' },
    { key: 'certifications', href: '#certifications' },
    { key: 'sectors', href: '#sectors' },
    { key: 'contact', href: '#contact' }
  ];

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20 || !isHome);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [isHome]);

  const goto = (href) => {
    setOpen(false);
    if (!isHome) {
      navigate('/');
      setTimeout(() => {
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200' : 'bg-transparent'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#0b2545] flex items-center justify-center">
              <span className="text-[#f7a11a] font-black text-lg">U</span>
            </div>
            <div className="flex flex-col leading-tight">
              <span className={`font-bold text-sm tracking-wide ${scrolled ? 'text-slate-900' : 'text-white'}`}>UDAAN</span>
              <span className={`text-[10px] tracking-[0.2em] uppercase ${scrolled ? 'text-slate-500' : 'text-slate-200'}`}>Worldwide Export</span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((item) => (
              <button key={item.key} onClick={() => goto(item.href)} className={`text-sm font-medium transition-colors hover:text-[#f7a11a] ${scrolled ? 'text-slate-700' : 'text-white/90'}`}>
                {t(`nav.${item.key}`)}
              </button>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <LangSwitcher dark={scrolled} />
            <Button onClick={() => goto('#quote')} className="bg-[#f7a11a] hover:bg-[#e69108] text-slate-900 font-semibold px-6 h-11 rounded-none tracking-wide">
              {t('cta.getQuote')}
            </Button>
          </div>

          <button className={`lg:hidden p-2 ${scrolled ? 'text-slate-900' : 'text-white'}`} onClick={() => setOpen(!open)} aria-label="menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {open && (
          <div className="lg:hidden bg-white border-t border-slate-200 py-4">
            {navItems.map((item) => (
              <button key={item.key} onClick={() => goto(item.href)} className="block w-full text-left px-4 py-3 text-slate-700 hover:bg-slate-50 hover:text-[#f7a11a] font-medium">
                {t(`nav.${item.key}`)}
              </button>
            ))}
            <div className="px-4 pt-3 flex items-center gap-3">
              <LangSwitcher dark />
              <Button onClick={() => goto('#quote')} className="flex-1 bg-[#f7a11a] hover:bg-[#e69108] text-slate-900 font-semibold h-11 rounded-none">
                {t('cta.getQuote')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
